// Company-field settings shared by the form editor and the landing page editor's legacy form
// editor. Each toggle returns a new fields array and leaves the input untouched.
import AppService from 'App/legacy/AppService';

type FormField = { name: string; options?: string | null; [key: string]: unknown };
type Category = { id: number };

const BISNODE = 'Prefs.bisnodeAutocomplete';
const COMPANY_FROM_CATEGORY = 'Prefs.companyFromCategory';
const MATCH_ON_SELECT = 'Prefs.matchOnCompanySelect';

const pref = (name: string) => ({ title: `form.${name}`, name, type: 'checkbox' });

// The account categories an admin has enabled as company-select sources.
export const getFormExposedAccountCategories = () =>
	(AppService.getCategories('account') || []).filter(c => c.exposeInForms);

// Client.name.options holds the company-select source categories: one id, or a comma-separated
// list. Forms published before multi-category support hold a single id, which parses as a
// one-element list, so no migration is needed.
export const parseCompanySelectCategories = (options?: string | null) =>
	String(options || '')
		.split(',')
		.map(part => parseInt(part, 10))
		.filter(id => !!id);

const has = (fields: FormField[], name: string) => fields.some(f => f.name === name);
const without = (fields: FormField[], ...names: string[]) => fields.filter(f => !names.includes(f.name));
const setCompanyOptions = (fields: FormField[], options: string | null) =>
	fields.map(f => (f.name === 'Client.name' ? { ...f, options } : f));
const disableCompanySelect = (fields: FormField[]) =>
	setCompanyOptions(without(fields, COMPANY_FROM_CATEGORY, MATCH_ON_SELECT), null);

// Bisnode autocomplete and company-from-category are mutually exclusive on the company field:
// enabling one disables the other.
export const toggleBisnodeAutocomplete = (fields: FormField[]): FormField[] => {
	if (has(fields, BISNODE)) {
		return without(fields, BISNODE);
	}
	const next = [...fields, pref(BISNODE)];
	return has(fields, COMPANY_FROM_CATEGORY) ? disableCompanySelect(next) : next;
};

export const toggleMatchOnCompanySelect = (fields: FormField[]): FormField[] =>
	has(fields, MATCH_ON_SELECT) ? without(fields, MATCH_ON_SELECT) : [...fields, pref(MATCH_ON_SELECT)];

export const toggleCompanyFromCategory = (fields: FormField[], exposedCategories: Category[]): FormField[] => {
	if (has(fields, COMPANY_FROM_CATEGORY)) {
		return disableCompanySelect(fields);
	}
	const next: FormField[] = [
		...without(fields, BISNODE),
		pref(COMPANY_FROM_CATEGORY),
		...(has(fields, MATCH_ON_SELECT) ? [] : [pref(MATCH_ON_SELECT)])
	];
	// Default to the first form-exposed category so the picker is never enabled-but-empty
	// (an unset category renders the company field as a plain input with no dropdown).
	const nameField = next.find(f => f.name === 'Client.name');
	if (nameField && !nameField.options && exposedCategories.length) {
		return setCompanyOptions(next, String(exposedCategories[0].id));
	}
	return next;
};

// Returns the new Client.name.options, or null when the toggle would empty the list: an empty
// list renders the company field as a plain input with no picker, which is the enabled-but-empty
// state the default pick exists to avoid.
export const toggleCompanySelectCategory = (options: string | null | undefined, categoryId: number) => {
	const selected = parseCompanySelectCategories(options);
	const next = selected.includes(categoryId) ? selected.filter(id => id !== categoryId) : [...selected, categoryId];
	return next.length ? next.join(',') : null;
};

// Opt-in fields keep their opt-in id in `options`, and so does Client.name for its company-select
// categories, so only opt-in fields may count when deciding which opt-ins are already on a form.
export const usedOptInIds = (fields: FormField[]) =>
	fields.filter(f => f.datatype === 'optIn').map(f => String(f.options));
